import React, { useState } from 'react';

function NewReservation({ rooms, onSubmit, onCancel, preselectedRoomId = null }) {
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    room_id: preselectedRoomId ? preselectedRoomId.toString() : '',
    check_in: '',
    check_out: ''
  });

  const [errors, setErrors] = useState({});
  const [estimatedCost, setEstimatedCost] = useState(0);

  // Calculer le coût estimé quand les dates ou la chambre changent
  React.useEffect(() => {
    if (formData.room_id && formData.check_in && formData.check_out) {
      const room = rooms.find(r => r.id === parseInt(formData.room_id));
      if (room) {
        const checkIn = new Date(formData.check_in);
        const checkOut = new Date(formData.check_out);
        const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
        if (nights > 0) {
          setEstimatedCost(nights * room.price_per_night);
        }
      }
    }
  }, [formData.room_id, formData.check_in, formData.check_out, rooms]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.guest_name.trim()) {
      newErrors.guest_name = 'Le nom est obligatoire';
    }

    if (formData.guest_email && !/\S+@\S+\.\S+/.test(formData.guest_email)) {
      newErrors.guest_email = 'Email invalide';
    }

    if (!formData.room_id) {
      newErrors.room_id = 'Veuillez sélectionner une chambre';
    }

    if (!formData.check_in) {
      newErrors.check_in = 'Date d\'arrivée obligatoire';
    }

    if (!formData.check_out) {
      newErrors.check_out = 'Date de départ obligatoire';
    }

    if (formData.check_in && formData.check_out) {
      const checkIn = new Date(formData.check_in);
      const checkOut = new Date(formData.check_out);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (checkIn < today) {
        newErrors.check_in = 'La date d\'arrivée ne peut pas être dans le passé';
      }

      if (checkOut <= checkIn) {
        newErrors.check_out = 'La date de départ doit être après l\'arrivée';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit({
        ...formData,
        room_id: parseInt(formData.room_id)
      });
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMinCheckOut = () => {
    if (formData.check_in) {
      const checkIn = new Date(formData.check_in);
      checkIn.setDate(checkIn.getDate() + 1);
      return checkIn.toISOString().split('T')[0];
    }
    return getMinDate();
  };

  const calculateNights = () => {
    if (formData.check_in && formData.check_out) {
      const checkIn = new Date(formData.check_in);
      const checkOut = new Date(formData.check_out);
      const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      return nights > 0 ? nights : 0;
    }
    return 0;
  };

  const selectedRoom = rooms.find(r => r.id === parseInt(formData.room_id));
  const preselectedRoom = preselectedRoomId ? rooms.find(r => r.id === preselectedRoomId) : null;

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2>Nouvelle Réservation</h2>
          {preselectedRoom && (
            <div style={{
              fontSize: '0.9rem',
              color: 'var(--primary-blue)',
              marginTop: '0.5rem',
              fontWeight: '500'
            }}>
              ⚡ Réservation rapide pour la chambre {preselectedRoom.room_number}
            </div>
          )}
        </div>
        <button className="btn btn-secondary" onClick={onCancel}>
          ← Retour
        </button>
      </div>

      <form onSubmit={handleSubmit} className="form" style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div className="form-group">
          <label htmlFor="guest_name">Nom du Client *</label>
          <input
            type="text"
            id="guest_name"
            name="guest_name"
            value={formData.guest_name}
            onChange={handleChange}
            placeholder="Nom Prénom"
            style={{ borderColor: errors.guest_name ? '#dc3545' : '#ddd' }}
          />
          {errors.guest_name && (
            <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
              {errors.guest_name}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="guest_email">Email</label>
          <input
            type="email"
            id="guest_email"
            name="guest_email"
            value={formData.guest_email}
            onChange={handleChange}
            placeholder="email@exemple.com"
            style={{ borderColor: errors.guest_email ? '#dc3545' : '#ddd' }}
          />
          {errors.guest_email && (
            <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
              {errors.guest_email}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="room_id">Chambre *</label>
          <select
            id="room_id"
            name="room_id"
            value={formData.room_id}
            onChange={handleChange}
            style={{ borderColor: errors.room_id ? '#dc3545' : '#ddd' }}
          >
            <option value="">Sélectionner une chambre</option>
            {rooms
              .sort((a, b) => a.room_number.localeCompare(b.room_number))
              .map(room => (
                <option key={room.id} value={room.id}>
                  {room.room_number} - {room.type} (€{room.price_per_night}/nuit)
                </option>
              ))}
          </select>
          {errors.room_id && (
            <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
              {errors.room_id}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="form-group">
            <label htmlFor="check_in">Date d'Arrivée *</label>
            <input
              type="date"
              id="check_in"
              name="check_in"
              value={formData.check_in}
              onChange={handleChange}
              min={getMinDate()}
              style={{ borderColor: errors.check_in ? '#dc3545' : '#ddd' }}
            />
            {errors.check_in && (
              <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                {errors.check_in}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="check_out">Date de Départ *</label>
            <input
              type="date"
              id="check_out"
              name="check_out"
              value={formData.check_out}
              onChange={handleChange}
              min={getMinCheckOut()}
              style={{ borderColor: errors.check_out ? '#dc3545' : '#ddd' }}
            />
            {errors.check_out && (
              <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                {errors.check_out}
              </div>
            )}
          </div>
        </div>

        {/* Résumé de la réservation */}
        {selectedRoom && formData.check_in && formData.check_out && calculateNights() > 0 && (
          <div style={{
            background: '#f8f9fa',
            padding: '1rem',
            borderRadius: '4px',
            marginTop: '1rem',
            border: '1px solid #e9ecef'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Résumé de la Réservation</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
              <div><strong>Chambre:</strong> {selectedRoom.room_number} ({selectedRoom.type})</div>
              <div><strong>Prix/nuit:</strong> €{selectedRoom.price_per_night}</div>
              <div><strong>Nombre de nuits:</strong> {calculateNights()}</div>
              <div><strong>Total estimé:</strong> <strong style={{ color: '#28a745' }}>€{estimatedCost}</strong></div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
            Créer la Réservation
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Annuler
          </button>
        </div>
      </form>

      {/* Info sur les chambres disponibles */}
      <div style={{ marginTop: '2rem', fontSize: '0.9rem', color: '#666' }}>
        <strong>{rooms.length} chambres disponibles:</strong>
        <div style={{ marginTop: '0.5rem' }}>
          {rooms.reduce((acc, room) => {
            acc[room.type] = (acc[room.type] || 0) + 1;
            return acc;
          }, Object.create(null)) && 
            Object.entries(rooms.reduce((acc, room) => {
              acc[room.type] = (acc[room.type] || 0) + 1;
              return acc;
            }, {})).map(([type, count]) => (
              <span key={type} style={{ marginRight: '1rem' }}>
                {count} {type}(s)
              </span>
            ))
          }
        </div>
      </div>
    </div>
  );
}

export default NewReservation;